import { useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
	type Entry,
	type EntryStatus,
	useCacheMutations,
} from "@/shared/cache";
import { FEATURE_FLAGS } from "@/shared/config/featureFlags";
import { useFeatureFlag } from "@/shared/hooks/useFeatureFlags";
import {
	canModifyExportedEntry,
	isTysonMendesProLawCutoffLocked,
	PROLAW_CUTOFF_LOCK_MESSAGE,
} from "@/shared/utils/exportedEntryMutation";
import { useApi } from "@/shared/utils/request";
import type { TimeEntryFormValues } from "@/v2/components/entry-form/schema";
import { type AddPayload, addAction } from "@/v2/hooks/actions/add";
import { combineAction } from "@/v2/hooks/actions/combine";
import { deleteAction } from "@/v2/hooks/actions/delete";
import { duplicateAction } from "@/v2/hooks/actions/duplicate";
import { type EditPayload, editAction } from "@/v2/hooks/actions/edit";
import { type EntryIdInput, formatEntryId } from "@/v2/hooks/actions/entryId";
import { type ExportResult, exportAction } from "@/v2/hooks/actions/export";
import {
	createGroupedAction,
	type MutationAction,
	runMutationAction,
} from "@/v2/hooks/actions/mutationAction";
import { restoreDeletedAction } from "@/v2/hooks/actions/restore";
import {
	setStatusAction,
	setStatusManyAction,
} from "@/v2/hooks/actions/setStatus";
import type { ActionDeps } from "@/v2/hooks/actions/types";
import { useAttachmentCommit } from "@/v2/hooks/useEntryAttachments";
import { formDateToEntryDate } from "@/v2/lib/dates";
import {
	groupEntriesByTimekeeper,
	MIXED_TIMEKEEPER_SELECTION_MESSAGE,
	selectionTimekeeperId,
} from "@/v2/lib/selectionTimekeeperId";
import { AddEntryDialog } from "@/v2/pages/Entries/components/AddEntryDialog";
import { CombineDialog } from "@/v2/pages/Entries/components/CombineDialog";
import { EditEntryDialog } from "@/v2/pages/Entries/components/EditEntryDialog";
import type { Timekeeper } from "../timekeepers";

export type RowActions = {
	onEdit: (entry: Entry) => void;
	onDuplicate: (entries: Entry[]) => void;
	onExport: (entries: Entry[]) => void;
	onDelete: (entries: Entry[]) => void;
	onRestoreDeleted: (entries: Entry[]) => void;
	onMarkState: (entries: Entry[], target: EntryStatus) => void;
};

type Props = {
	selectedEntries: Entry[];
	clearSelection: () => void;
	addDefaultDate: Date;
	timekeeperId?: string | null;
	timekeepers: readonly Timekeeper[] | null;
};

type AddEntryPayload = {
	id: number;
	date: string;
	matter: string | null;
	matter_id: string;
	description: string;
	duration: number;
	task: string;
	activity: string;
};

type EditEntryPayload = Omit<AddEntryPayload, "id"> & {
	id: EntryIdInput;
	timekeeperId?: string | null;
};

const DUPLICATE_DRAFT_TOAST = {
	loading: "Duplicating entry...",
	success: "Entry duplicated",
	error: "Failed to duplicate entry",
} as const satisfies MutationAction["toast"];

export function useEntryActions({
	selectedEntries,
	clearSelection,
	addDefaultDate,
	timekeeperId,
	timekeepers,
}: Props) {
	const { makeApiRequest } = useApi();
	const { refresh, optimisticUpdate } = useCacheMutations();
	const commitAttachments = useAttachmentCommit();
	const canUploadFiles = useFeatureFlag(FEATURE_FLAGS.FILE_UPLOAD);

	const updateEntries = useCallback(
		(updater: (prev: Entry[]) => Entry[]) =>
			optimisticUpdate("entries", updater),
		[optimisticUpdate],
	);
	const refreshEntries = useCallback(() => refresh("entries"), [refresh]);

	// ── Entry mutations + stable deps ────────────────────────
	//
	// Each action factory returns an executable mutation with its optimistic
	// rollback and toast copy attached. TanStack Query runs the mutation;
	// failures roll back whichever cache the action touched.
	const depsRef = useRef<ActionDeps>({
		makeApiRequest,
		updateEntries,
		refreshEntries,
	});
	depsRef.current = {
		makeApiRequest,
		updateEntries,
		refreshEntries,
	};

	const stableDeps = useRef<ActionDeps>({
		makeApiRequest: (opts) => depsRef.current.makeApiRequest(opts),
		updateEntries: (updater) => depsRef.current.updateEntries(updater),
		refreshEntries: () => depsRef.current.refreshEntries(),
	}).current;

	const { mutate: runAction } = useMutation({
		mutationKey: ["entries", "actions"],
		mutationFn: runMutationAction,
	});

	// ── Dialog state (this is the only thing the page owns beyond
	// dispatching entry mutations) ───────────────────────────
	const [addOpen, setAddOpen] = useState(false);
	const [addDialogDate, setAddDialogDate] = useState(addDefaultDate);
	const [addTimerPrefillDuration, setAddTimerPrefillDuration] = useState<
		number | null
	>(null);
	const [editOpen, setEditOpen] = useState(false);
	const [selectedEntry, setSelectedEntry] = useState<Entry | null>(null);
	const [combineOpen, setCombineOpen] = useState(false);
	// Snapshot of the selection captured when the Combine button is clicked, so
	// the dialog's list is stable even if the selection changes underneath it.
	const [combineEntries, setCombineEntries] = useState<Entry[]>([]);

	// ── Per-row export errors ───────────────────────────────────
	//
	// Session-scoped map of `ent_<n>` → failure reason, mirroring
	// `ActionsProvider.failedExports`. There's no server
	// field for this, so it lives in page state: a failed export draws the red
	// ribbon on the row (EntriesTable) and the "Export Failed" banner inside the
	// edit dialog. Cleared when the row re-exports successfully or
	// is edited.
	const [failedExports, setFailedExports] = useState<Map<string, string>>(
		new Map(),
	);

	const handleExportComplete = useCallback((result: ExportResult) => {
		setFailedExports((prev) => {
			const next = new Map(prev);
			for (const id of result.exportedIds) next.delete(formatEntryId(id));
			for (const { id, error } of result.failedEntries)
				next.set(formatEntryId(id), error);
			return next;
		});
	}, []);

	const clearError = useCallback((id: EntryIdInput) => {
		setFailedExports((prev) => {
			const key = formatEntryId(id);
			if (!prev.has(key)) return prev;
			const next = new Map(prev);
			next.delete(key);
			return next;
		});
	}, []);

	// ── Export (atomic bulk) ─────────────────────────────────
	//
	// Export now runs through the same mutation path as every other entry
	// mutation: `POST /v2/entries/export` flips the whole selection to the
	// terminal `exported` state in one call. The mutation owns the toast and the
	// in-flight/loading state, so the old per-row `exportingIds` set (which
	// guarded opening the edit dialog on an in-flight row) is gone.
	//
	const performExport = useCallback(
		(ids: EntryIdInput[], timekeeperId?: string | null) => {
			if (ids.length === 0) return;
			runAction(
				exportAction(
					ids,
					stableDeps,
					handleExportComplete,
					undefined,
					timekeeperId,
				),
			);
			clearSelection();
		},
		[runAction, stableDeps, clearSelection, handleExportComplete],
	);

	// ── State transitions ───────────────────────────────────
	//
	// Tracked ↔ reviewed flips use the cheap-path PATCH (single round-trip
	// per row, optimistic). Deletes go through the atomic bulk endpoint
	// (`POST /v2/entries/delete`) — same backend leg as the PATCH, but a
	// single request covers any selection size.
	const setStatus = useCallback(
		(
			id: EntryIdInput,
			target: "tracked" | "reviewed",
			timekeeperId?: string | null,
		) => {
			runAction(
				setStatusAction(id, target, stableDeps, undefined, timekeeperId),
			);
		},
		[runAction, stableDeps],
	);

	const performDelete = useCallback(
		(ids: EntryIdInput[], timekeeperId?: string | null) => {
			if (ids.length === 0) return;
			runAction(deleteAction(ids, stableDeps, timekeeperId));
		},
		[runAction, stableDeps],
	);

	const performRestoreDeleted = useCallback(
		(ids: EntryIdInput[], timekeeperId?: string | null) => {
			if (ids.length === 0) return;
			runAction(restoreDeletedAction(ids, stableDeps, timekeeperId));
		},
		[runAction, stableDeps],
	);

	const markState = useCallback(
		(entry: Entry, target: EntryStatus) => {
			if (entry.status === target) return;
			const id = entry.id;
			switch (target) {
				case "tracked":
				case "reviewed":
					setStatus(id, target, entry.timekeeperId);
					return;
				case "deleted":
					performDelete([id], entry.timekeeperId);
					return;
				case "exported":
					performExport([id], entry.timekeeperId);
					return;
			}
		},
		[performExport, performDelete, setStatus],
	);

	const groupedActions = useCallback(
		(
			entries: Entry[],
			build: (ids: EntryIdInput[], timekeeperId: string) => MutationAction,
			toastCopy: MutationAction["toast"],
		) => {
			const groups = groupEntriesByTimekeeper(entries);
			if (groups === false) {
				toast.error("Unable to determine the timekeeper for every entry");
				return false;
			}
			runAction(
				createGroupedAction(
					groups.map((group) =>
						build(
							group.entries.map((entry) => entry.id),
							group.timekeeperId,
						),
					),
					toastCopy,
				),
			);
			return true;
		},
		[runAction],
	);

	const performGroupedExport = useCallback(
		(entries: Entry[]) =>
			groupedActions(
				entries,
				(ids, ownerId) =>
					exportAction(
						ids,
						stableDeps,
						handleExportComplete,
						undefined,
						ownerId,
					),
				{
					loading: "Exporting entries…",
					success: "Entries exported",
					error: "Some timekeeper exports failed",
				},
			),
		[groupedActions, handleExportComplete, stableDeps],
	);

	const performGroupedDelete = useCallback(
		(entries: Entry[]) =>
			groupedActions(
				entries,
				(ids, ownerId) => deleteAction(ids, stableDeps, ownerId),
				{
					loading: "Deleting entries…",
					success: "Entries deleted",
					error: "Some timekeeper deletes failed",
				},
			),
		[groupedActions, stableDeps],
	);

	const performGroupedRestore = useCallback(
		(entries: Entry[]) =>
			groupedActions(
				entries,
				(ids, ownerId) => restoreDeletedAction(ids, stableDeps, ownerId),
				{
					loading: "Restoring entries…",
					success: "Entries restored",
					error: "Some timekeeper restores failed",
				},
			),
		[groupedActions, stableDeps],
	);

	const performGroupedDuplicate = useCallback(
		(entries: Entry[]) =>
			groupedActions(
				entries,
				(ids, ownerId) => duplicateAction(ids, stableDeps, ownerId),
				{
					loading: "Duplicating entries…",
					success: "Entries duplicated",
					error: "Some timekeeper duplicates failed",
				},
			),
		[groupedActions, stableDeps],
	);

	const performGroupedStatus = useCallback(
		(entries: Entry[], target: "tracked" | "reviewed") =>
			groupedActions(
				entries,
				(ids, ownerId) =>
					setStatusManyAction(ids, target, stableDeps, undefined, ownerId),
				{
					loading: "Updating entries…",
					success: "Entries updated",
					error: "Some timekeeper updates failed",
				},
			),
		[groupedActions, stableDeps],
	);

	// ── Add ────────────────────────────────────────────────
	//
	// We run a plain add and chain export on success so
	// add-then-export goes through the v2 export path.
	const handleSaveAdd = useCallback(
		(
			newEntry: AddEntryPayload,
			shouldExport: boolean,
			files: File[] = [],
			entryTimekeeperId: string | null = timekeeperId ?? null,
			toast?: MutationAction["toast"],
		) => {
			const payload: AddPayload = {
				...newEntry,
				date: formDateToEntryDate(newEntry.date),
				timekeeperId: entryTimekeeperId,
			};
			runAction(
				addAction(
					payload,
					stableDeps,
					(newEntryId) => {
						if (newEntryId != null && files.length > 0) {
							void commitAttachments(
								formatEntryId(newEntryId),
								{ files, removedIds: [] },
								payload.timekeeperId,
							);
						}
						if (shouldExport && newEntryId != null) {
							performExport([newEntryId], payload.timekeeperId);
						}
					},
					toast,
				),
			);
		},
		[commitAttachments, runAction, stableDeps, performExport, timekeeperId],
	);

	// ── Edit ───────────────────────────────────────────────
	const handleSaveEdit = useCallback(
		(
			updated: EditEntryPayload,
			shouldExport: boolean,
			nextStatus?: EntryStatus,
		) => {
			const payload: EditPayload = {
				...updated,
				date: formDateToEntryDate(updated.date),
				timekeeperId:
					updated.timekeeperId ?? selectedEntry?.timekeeperId ?? null,
			};
			const base = editAction(payload, stableDeps);
			// Chain validated export on success without changing editAction's
			// signature. The base action's onSuccess refreshes the entries cache; we
			// fire-and-forget the export afterward.
			const augmented: MutationAction = {
				...base,
				onSuccess: () => {
					base.onSuccess?.();
					// A successful edit supersedes a prior export failure for this
					// row — drop its ribbon/banner. A re-export (shouldExport) that
					// fails again will re-populate the map via handleExportComplete.
					clearError(updated.id);
					if (
						!shouldExport &&
						(nextStatus === "tracked" || nextStatus === "reviewed") &&
						selectedEntry?.status !== nextStatus
					) {
						runAction(
							setStatusAction(
								updated.id,
								nextStatus,
								stableDeps,
								undefined,
								payload.timekeeperId,
							),
						);
						setSelectedEntry(
							selectedEntry ? { ...selectedEntry, status: nextStatus } : null,
						);
					}
					if (shouldExport) performExport([updated.id], payload.timekeeperId);
				},
			};
			runAction(augmented);
		},
		[runAction, stableDeps, performExport, clearError, selectedEntry],
	);

	// ── Toolbar entrypoints ─────────────────────────────────
	const closeAddDialog = useCallback(() => {
		setAddOpen(false);
		setAddTimerPrefillDuration(null);
	}, []);

	const handleAddOpenChange = useCallback(
		(open: boolean) => {
			setAddOpen(open);
			if (open) {
				setAddDialogDate(addDefaultDate);
			}
			if (!open) {
				setAddTimerPrefillDuration(null);
			}
		},
		[addDefaultDate],
	);

	const handleAddSubmit = useCallback(
		(
			values: TimeEntryFormValues,
			files: File[],
			selectedTimekeeperId: string | null,
		) => {
			handleSaveAdd(
				{
					id: 0,
					date: format(values.date, "MM/dd/yyyy"),
					matter: values.matter,
					matter_id: values.matter_id,
					description: values.description,
					duration: values.duration,
					task: values.task,
					activity: values.activity ?? "",
				},
				false,
				canUploadFiles ? files : [],
				selectedTimekeeperId,
			);
			closeAddDialog();
		},
		[canUploadFiles, handleSaveAdd, closeAddDialog],
	);

	const handleAddExport = useCallback(
		(
			values: TimeEntryFormValues,
			files: File[],
			selectedTimekeeperId: string | null,
		) => {
			handleSaveAdd(
				{
					id: 0,
					date: format(values.date, "MM/dd/yyyy"),
					matter: values.matter,
					matter_id: values.matter_id,
					description: values.description,
					duration: values.duration,
					task: values.task,
					activity: values.activity ?? "",
				},
				true,
				canUploadFiles ? files : [],
				selectedTimekeeperId,
			);
			closeAddDialog();
		},
		[canUploadFiles, handleSaveAdd, closeAddDialog],
	);

	const handleAddDuplicate = useCallback(
		(
			values: TimeEntryFormValues,
			files: File[],
			selectedTimekeeperId: string | null,
		) => {
			handleSaveAdd(
				{
					id: 0,
					date: format(values.date, "MM/dd/yyyy"),
					matter: values.matter,
					matter_id: values.matter_id,
					description: values.description,
					duration: values.duration,
					task: values.task,
					activity: values.activity ?? "",
				},
				false,
				canUploadFiles ? files : [],
				selectedTimekeeperId,
			);
		},
		[canUploadFiles, handleSaveAdd],
	);

	const onAdd = useCallback(() => {
		setAddDialogDate(addDefaultDate);
		setAddTimerPrefillDuration(null);
		setAddOpen(true);
	}, [addDefaultDate]);

	// Open the Add dialog prefilled with the actual tracked duration (seconds) —
	// used by the toolbar timer when an entry is created from tracked time.
	const onAddWithDuration = useCallback(
		(durationSeconds: number) => {
			setAddDialogDate(addDefaultDate);
			setAddTimerPrefillDuration(durationSeconds > 0 ? durationSeconds : null);
			setAddOpen(true);
		},
		[addDefaultDate],
	);

	// All-or-nothing semantics: if ANY selected row is exported, normal modify
	// actions (Merge, Group, Collapse, Export) are locked out. Tyson Mendes may
	// still delete post-cutoff exported rows during ProLaw reconciliation. The
	// toolbar buttons disable in EntriesView via `selectionHasExported`; the
	// handlers below short-circuit defensively so a keyboard shortcut or
	// race can't bypass the visual disable. Duplicate is the only bulk
	// action allowed on exported rows.
	const selectionHasExported = useMemo(
		() => selectedEntries.some((e) => e.status === "exported"),
		[selectedEntries],
	);
	const selectionHasLockedExported = useMemo(
		() =>
			selectedEntries.some(
				(entry) =>
					entry.status === "exported" && !canModifyExportedEntry(entry),
			),
		[selectedEntries],
	);
	const selectionHasProLawCutoffLocked = useMemo(
		() =>
			selectedEntries.some((entry) =>
				isTysonMendesProLawCutoffLocked(entry),
			),
		[selectedEntries],
	);
	const selectedDeletedCount = useMemo(
		() => selectedEntries.filter((e) => e.status === "deleted").length,
		[selectedEntries],
	);
	const selectionHasDeleted = selectedDeletedCount > 0;

	const onExportAction = useCallback(() => {
		if (selectedEntries.length === 0) {
			toast.error("Please select entries to export");
			return;
		}
		if (selectionHasDeleted) {
			toast.error("Restore entries before exporting");
			return;
		}
		if (selectionHasExported) return; // defense-in-depth — button is disabled
		performGroupedExport(selectedEntries);
		clearSelection();
	}, [
		selectedEntries,
		selectionHasDeleted,
		selectionHasExported,
		performGroupedExport,
		clearSelection,
	]);

	const onRestoreDeletedAction = useCallback(() => {
		const deletedEntries = selectedEntries.filter(
			(e) => e.status === "deleted",
		);
		if (deletedEntries.length === 0) {
			toast.error("Please select deleted entries to restore");
			return;
		}
		performGroupedRestore(deletedEntries);
		clearSelection();
	}, [selectedEntries, performGroupedRestore, clearSelection]);

	const onDeleteAction = useCallback(() => {
		if (selectedEntries.length === 0) {
			toast.error("Please select entries to delete");
			return;
		}
		if (selectionHasDeleted) return;
		if (selectionHasLockedExported) {
			if (selectionHasProLawCutoffLocked) {
				toast.error(PROLAW_CUTOFF_LOCK_MESSAGE);
			}
			return;
		}
		performGroupedDelete(selectedEntries);
		clearSelection();
	}, [
		selectedEntries,
		selectionHasDeleted,
		selectionHasLockedExported,
		selectionHasProLawCutoffLocked,
		performGroupedDelete,
		clearSelection,
	]);

	const onMarkReviewedAction = useCallback(() => {
		if (selectedEntries.length === 0) {
			toast.error("Please select entries to mark reviewed");
			return;
		}
		if (selectionHasDeleted) return;
		if (selectionHasExported) return;
		performGroupedStatus(selectedEntries, "reviewed");
		clearSelection();
	}, [
		selectedEntries,
		selectionHasDeleted,
		selectionHasExported,
		performGroupedStatus,
		clearSelection,
	]);

	const onMarkTrackedAction = useCallback(() => {
		if (selectedEntries.length === 0) {
			toast.error("Please select entries to mark tracked");
			return;
		}
		if (selectionHasDeleted) return;
		if (selectionHasExported) return;
		performGroupedStatus(selectedEntries, "tracked");
		clearSelection();
	}, [
		selectedEntries,
		selectionHasDeleted,
		selectionHasExported,
		performGroupedStatus,
		clearSelection,
	]);

	const onDuplicateAction = useCallback(() => {
		// Duplicate is the one bulk action that's valid on every state —
		// including exported. v2 duplicate produces a fresh `tracked` copy
		// regardless of source state.
		if (selectedEntries.length === 0) {
			toast.error("Please select entries to duplicate");
			return;
		}
		performGroupedDuplicate(selectedEntries);
		clearSelection();
	}, [selectedEntries, performGroupedDuplicate, clearSelection]);

	const onCombineAction = useCallback(() => {
		if (selectedEntries.length < 2) {
			toast.error("Please select at least 2 entries to combine");
			return;
		}
		if (selectionHasDeleted) return;
		if (selectionHasExported) return;
		const timekeeperId = selectionTimekeeperId(selectedEntries);
		if (timekeeperId === false) {
			toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
			return;
		}
		setCombineEntries(selectedEntries);
		setCombineOpen(true);
	}, [selectedEntries, selectionHasDeleted, selectionHasExported]);

	const handleConfirmCombine = useCallback(
		(ids: string[], matterId: string, mainEntryId: string) => {
			const timekeeperId = selectionTimekeeperId(combineEntries);
			if (timekeeperId === false) {
				toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
				return;
			}
			runAction(
				combineAction(ids, matterId, mainEntryId, stableDeps, timekeeperId),
			);
			setCombineOpen(false);
			clearSelection();
		},
		[combineEntries, runAction, stableDeps, clearSelection],
	);

	const onRowClick = useCallback((entry: Entry) => {
		// The mutation layer owns the in-flight/loading state now, so there's no
		// per-row export guard here — opening the edit dialog on a row that's
		// mid-export is harmless; the server rejects edits on exported rows and
		// the cache refresh reconciles.
		setSelectedEntry(entry);
		setEditOpen(true);
	}, []);

	// Refs to keep rowActions stable across renders. RowContextMenuContent
	// mounts on every row, so an unstable rowActions reference would re-run
	// each row's menu component on every parent render.
	const onRowClickRef = useRef(onRowClick);
	const performGroupedDuplicateRef = useRef(performGroupedDuplicate);
	const performGroupedExportRef = useRef(performGroupedExport);
	const performGroupedDeleteRef = useRef(performGroupedDelete);
	const performGroupedRestoreRef = useRef(performGroupedRestore);
	const performGroupedStatusRef = useRef(performGroupedStatus);
	const markStateRef = useRef(markState);
	const clearSelectionRef = useRef(clearSelection);
	onRowClickRef.current = onRowClick;
	performGroupedDuplicateRef.current = performGroupedDuplicate;
	performGroupedExportRef.current = performGroupedExport;
	performGroupedDeleteRef.current = performGroupedDelete;
	performGroupedRestoreRef.current = performGroupedRestore;
	performGroupedStatusRef.current = performGroupedStatus;
	markStateRef.current = markState;
	clearSelectionRef.current = clearSelection;

	const rowActions = useMemo<RowActions>(() => {
		const eligibleForTarget = (
			entries: Entry[],
			target: EntryStatus,
		): Entry[] =>
			entries.filter((entry) => {
				if (entry.status === target || entry.status === "deleted") return false;
				if (entry.status !== "exported") return true;
				return target === "deleted" && canModifyExportedEntry(entry);
			});

		return {
			onEdit: (entry) => onRowClickRef.current(entry),
			onDuplicate: (entries) => {
				performGroupedDuplicateRef.current(entries);
			},
			onExport: (entries) => {
				performGroupedExportRef.current(eligibleForTarget(entries, "exported"));
			},
			onDelete: (entries) => {
				performGroupedDeleteRef.current(eligibleForTarget(entries, "deleted"));
				if (entries.length > 1) clearSelectionRef.current();
			},
			onRestoreDeleted: (entries) => {
				const deletedEntries = entries.filter(
					(entry) => entry.status === "deleted",
				);
				performGroupedRestoreRef.current(deletedEntries);
			},
			onMarkState: (entries, target) => {
				const eligibleEntries = eligibleForTarget(entries, target);
				if (eligibleEntries.length === 0) return;
				if (eligibleEntries.length === 1) {
					markStateRef.current(eligibleEntries[0], target);
					return;
				}
				switch (target) {
					case "tracked":
					case "reviewed":
						performGroupedStatusRef.current(eligibleEntries, target);
						clearSelectionRef.current();
						return;
					case "deleted":
						performGroupedDeleteRef.current(eligibleEntries);
						clearSelectionRef.current();
						return;
					case "exported":
						performGroupedExportRef.current(eligibleEntries);
						return;
				}
			},
		};
	}, []);

	const saveEditDialogValues = useCallback(
		(
			entryId: string,
			values: TimeEntryFormValues,
			shouldExport: boolean,
			nextStatus?: EntryStatus,
		) => {
			if (
				selectedEntry?.id === entryId &&
				!canModifyExportedEntry(selectedEntry)
			) {
				if (isTysonMendesProLawCutoffLocked(selectedEntry)) {
					toast.error(PROLAW_CUTOFF_LOCK_MESSAGE);
				}
				return;
			}
			handleSaveEdit(
				{
					id: entryId,
					date: values.date ? format(values.date, "MM/dd/yyyy") : "",
					matter: values.matter,
					matter_id: values.matter_id,
					description: values.description,
					duration: values.duration,
					task: values.task,
					activity: values.activity ?? "",
				},
				shouldExport,
				nextStatus,
			);
			setEditOpen(false);
		},
		[handleSaveEdit, selectedEntry],
	);

	const dialogs: ReactNode = (
		<>
			<AddEntryDialog
				open={addOpen || addTimerPrefillDuration !== null}
				date={addDialogDate}
				timerPrefillDuration={addTimerPrefillDuration}
				onOpenChange={handleAddOpenChange}
				onSubmit={handleAddSubmit}
				onExport={handleAddExport}
				onDuplicate={handleAddDuplicate}
				canUploadFiles={canUploadFiles}
				timekeepers={timekeepers}
				defaultTimekeeperId={timekeeperId}
			/>
			<EditEntryDialog
				entry={editOpen ? selectedEntry : null}
				failedExports={failedExports}
				status={selectedEntry?.status}
				canUploadFiles={canUploadFiles}
				onOpenChange={(open) => {
					setEditOpen(open);
					if (!open) setSelectedEntry(null);
				}}
				onResolveFeedback={() => undefined}
				onSubmit={(entry, values, status) =>
					saveEditDialogValues(entry.id, values, false, status)
				}
				onExport={(entry, values) =>
					saveEditDialogValues(entry.id, values, true)
				}
				onDuplicate={(entry, values) => {
					handleSaveAdd(
						{
							id: 0,
							date: values.date ? format(values.date, "MM/dd/yyyy") : "",
							matter: values.matter,
							matter_id: values.matter_id,
							description: values.description,
							duration: values.duration,
							task: values.task,
							activity: values.activity ?? "",
						},
						false,
						[],
						entry.timekeeperId ?? timekeeperId ?? null,
						DUPLICATE_DRAFT_TOAST,
					);
					setEditOpen(false);
					setSelectedEntry(null);
				}}
				onDelete={(entry) => {
					performDelete([entry.id], entry.timekeeperId);
					setEditOpen(false);
					setSelectedEntry(null);
				}}
				onRestore={(entry) => {
					performRestoreDeleted([entry.id], entry.timekeeperId);
					setSelectedEntry(
						selectedEntry ? { ...selectedEntry, status: "tracked" } : null,
					);
				}}
			/>
			<CombineDialog
				open={combineOpen}
				entries={combineEntries}
				onOpenChange={setCombineOpen}
				onConfirm={handleConfirmCombine}
			/>
		</>
	);

	return {
		onAdd,
		onAddWithDuration,
		onExport: onExportAction,
		onRestoreDeleted: onRestoreDeletedAction,
		onDelete: onDeleteAction,
		onDuplicate: onDuplicateAction,
		onCombine: onCombineAction,
		onMarkReviewed: onMarkReviewedAction,
		onMarkTracked: onMarkTrackedAction,
		onRowClick,
		rowActions,
		dialogs,
		// EntriesView reads these to swap Export → Restore and to disable
		// normal modify toolbar buttons while selected rows are exported/deleted.
		// Duplicate stays clickable because v2 duplicate produces a fresh
		// `tracked` copy from any source.
		selectionHasExported,
		selectionHasLockedExported,
		selectionHasProLawCutoffLocked,
		selectionHasDeleted,
		selectedDeletedCount,
	};
}
